import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Form, Input, InputNumber, Button, Space, Tabs, message,
    Upload, Image, Select, DatePicker, Segmented, Modal,
} from 'antd';
import dayjs from 'dayjs';
import { UploadOutlined, PlusOutlined, DeleteOutlined, RightOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload';
import { useMutation } from '@apollo/client';
import { UPLOAD_ACTIVITY_ICON } from '../graphql/queries';
import type { UploadActivityIconResponse, UploadActivityIconInput } from '../graphql/queries';
import {
    parseActivityContent, parseRuleMatchRule,
    serializeActivityContent, serializeRules,
} from '../utils/activityConfig';
import type { ActivityFormValues, SerializedRule } from '../utils/activityConfig';
import type { CommonConfigType } from '../graphql/queries';


const hourOptions = Array.from({ length: 24 }, (_, h) => ({ label: `${h}`, value: h }));
// 与后端上限一致：每个活动最多 10 条规则
const MAX_RULES_PER_ACTIVITY = 10;

interface AppActivityDrawerProps {
    visible: boolean;
    editingConfigs: CommonConfigType[] | null;
    onClose: () => void;
    onSubmit: (values: { configValue: string; rules: SerializedRule[] }) => Promise<void>;
    submitting: boolean;
}

const AppActivityDrawer: React.FC<AppActivityDrawerProps> = ({
    visible,
    editingConfigs,
    onClose,
    onSubmit,
    submitting,
}) => {
    const [form] = Form.useForm<ActivityFormValues>();
    const [iconUrl, setIconUrl] = useState<string>('');
    const [iconUploading, setIconUploading] = useState(false);
    const [badgeUrl, setBadgeUrl] = useState<string>('');
    const [badgeUploading, setBadgeUploading] = useState(false);
    const [previewLink, setPreviewLink] = useState<string>('');
    const [activeTab, setActiveTab] = useState('basic');
    const [previewMode, setPreviewMode] = useState<'mobile' | 'desktop'>('mobile');
    const [previewModalOpen, setPreviewModalOpen] = useState(false);

    const [uploadActivityIcon] = useMutation<UploadActivityIconResponse, { input: UploadActivityIconInput }>(
        UPLOAD_ACTIVITY_ICON,
    );

    useEffect(() => {
        if (!visible) return;
        setActiveTab('basic');
        if (editingConfigs && editingConfigs.length > 0) {
            const content = parseActivityContent(editingConfigs[0].configValue);
            const rules = editingConfigs.map((c) => ({
                id: c.id,
                sortOrder: c.sortOrder,
                ...parseRuleMatchRule(c.matchRule),
            }));
            form.setFieldsValue({ ...content, rules });
            setIconUrl(content.icon ?? '');
            setBadgeUrl(content.badge ?? '');
            setPreviewLink(content.link ?? '');
        } else {
            form.resetFields();
            form.setFieldsValue({ rules: [{}] });
            setIconUrl('');
            setBadgeUrl('');
            setPreviewLink('');
        }
    }, [visible, editingConfigs, form]);

    const uploadImageFile = useCallback(async (file: File): Promise<string | null> => {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => {
                const result = reader.result as string;
                const b64 = result.includes(',') ? result.split(',')[1] : result;
                resolve(b64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        const { data } = await uploadActivityIcon({
            variables: {
                input: {
                    fileBase64: base64,
                    filename: file.name,
                    contentType: file.type,
                },
            },
        });
        return data?.uploadActivityIcon?.publicUrl ?? null;
    }, [uploadActivityIcon]);

    const handleIconUpload = useCallback(async (file: File) => {
        setIconUploading(true);
        try {
            const url = await uploadImageFile(file);
            if (url) {
                setIconUrl(url);
                form.setFieldValue('icon', url);
                message.success('图标上传成功');
            }
        } catch (e) {
            message.error('图标上传失败');
        } finally {
            setIconUploading(false);
        }
        return false;
    }, [uploadImageFile, form]);

    const handleBadgeUpload = useCallback(async (file: File) => {
        setBadgeUploading(true);
        try {
            const url = await uploadImageFile(file);
            if (url) {
                setBadgeUrl(url);
                form.setFieldValue('badge', url);
                message.success('角标上传成功');
            }
        } catch (e) {
            message.error('角标上传失败');
        } finally {
            setBadgeUploading(false);
        }
        return false;
    }, [uploadImageFile, form]);

    const handleBadgeClear = useCallback(() => {
        setBadgeUrl('');
        form.setFieldValue('badge', undefined);
    }, [form]);

    const handleSubmit = useCallback(async () => {
        const values = await form.validateFields();
        values.icon = iconUrl;
        values.badge = badgeUrl || undefined;
        const configValue = serializeActivityContent(values);
        const rules = serializeRules(values.rules);
        await onSubmit({ configValue, rules });
    }, [form, iconUrl, badgeUrl, onSubmit]);

    const openPreview = useCallback(() => {
        const link = (form.getFieldValue('link') ?? '').trim();
        if (!link) {
            message.info('请先填写活动链接');
            return;
        }
        setPreviewLink(link);
        setPreviewModalOpen(true);
    }, [form]);

    const isEdit = !!(editingConfigs && editingConfigs.length > 0);

    const previewNameZh = Form.useWatch('nameZh', form);

    const basicPreview = (
        <div
            style={{
                width: 360,
                maxWidth: '100%',
                background: '#fff',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                overflow: 'hidden',
                marginBottom: 16,
                cursor: 'pointer',
            }}
            onClick={openPreview}
            title="点击预览活动链接"
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    borderBottom: '1px solid #f5f5f5',
                }}
            >
                {iconUrl ? (
                    <Image src={iconUrl} width={28} height={28} preview={false} style={{ objectFit: 'contain' }} />
                ) : (
                    <div style={{
                        width: 28, height: 28, borderRadius: 6, background: '#f0f0f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#bbb', fontSize: 11, flex: 'none',
                    }}>图标</div>
                )}
                <span style={{ flex: 1, fontSize: 15, color: '#333', minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {previewNameZh || '活动名称'}
                </span>
                {badgeUrl && (
                    <img
                        src={badgeUrl}
                        alt="角标"
                        style={{ height: 20, objectFit: 'contain', flex: 'none' }}
                    />
                )}
                <RightOutlined style={{ color: '#ccc', fontSize: 13, flex: 'none' }} />
            </div>
        </div>
    );

    const basicTab = (
        <Form form={form} layout="vertical">
            {basicPreview}
            <Form.Item label="活动名称（中文）" name="nameZh" rules={[{ required: true, message: '请输入中文名称' }]}>
                <Input placeholder="双十一促销" />
            </Form.Item>
            <Form.Item label="活动名称（英文）" name="nameEn" rules={[{ required: true, message: '请输入英文名称' }]}>
                <Input placeholder="Double 11 Sale" />
            </Form.Item>
            <Form.Item label="活动名称（西班牙文）" name="nameEs" rules={[{ required: true, message: '请输入西班牙文名称' }]}>
                <Input placeholder="Oferta Doble 11" />
            </Form.Item>
            <Form.Item label="活动链接" name="link" rules={[{ required: true, message: '请输入活动链接' }]}>
                <Input placeholder="https://activity.example.com" />
            </Form.Item>
            <Form.Item label="活动图标" name="icon">
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Upload
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        showUploadList={false}
                        beforeUpload={(file: UploadFile) => handleIconUpload(file as unknown as File)}
                    >
                        <Button icon={<UploadOutlined />} loading={iconUploading}>
                            {iconUploading ? '上传中...' : '上传图标'}
                        </Button>
                    </Upload>
                    {iconUrl && (
                        <Image src={iconUrl} width={40} height={40} style={{ objectFit: 'contain' }} />
                    )}
                    {iconUrl && (
                        <span style={{ fontSize: 12, color: '#888', flex: 1, minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {iconUrl}
                        </span>
                    )}
                </div>
            </Form.Item>
            <Form.Item label="活动角标（可选，不传为空）" name="badge">
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Upload
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        showUploadList={false}
                        beforeUpload={(file: UploadFile) => handleBadgeUpload(file as unknown as File)}
                    >
                        <Button icon={<UploadOutlined />} loading={badgeUploading}>
                            {badgeUploading ? '上传中...' : '上传角标'}
                        </Button>
                    </Upload>
                    {badgeUrl && (
                        <Image src={badgeUrl} width={40} height={40} style={{ objectFit: 'contain' }} />
                    )}
                    {badgeUrl && (
                        <Button type="link" danger onClick={handleBadgeClear}>清除</Button>
                    )}
                </div>
            </Form.Item>
        </Form>
    );

    const grayscaleTab = (
        <Form form={form} layout="vertical">
            <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>
                多条规则之间为「或」关系：命中任意一条即展示该活动。每条规则内的多个条件为「且」关系。
            </div>
            <Form.List name="rules">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field, idx) => (
                            <div
                                key={field.key}
                                style={{
                                    border: '1px solid #e8e8e8',
                                    borderRadius: 8,
                                    padding: 16,
                                    marginBottom: 16,
                                    background: '#fcfcfc',
                                }}
                            >
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', marginBottom: 12,
                                }}>
                                    <span style={{ fontWeight: 600 }}>规则 {idx + 1}</span>
                                    <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        disabled={fields.length <= 1}
                                        onClick={() => remove(field.name)}
                                    >
                                        删除
                                    </Button>
                                </div>
                                <Form.Item name={[field.name, 'id']} hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item name={[field.name, 'sortOrder']} hidden>
                                    <InputNumber />
                                </Form.Item>
                                <Form.Item
                                    label="生效开始日期（含）"
                                    name={[field.name, 'startDate']}
                                    getValueProps={(v) => ({ value: v ? dayjs(v) : undefined })}
                                    normalize={(v) => (v ? dayjs(v).format('YYYY-MM-DD') : undefined)}
                                >
                                    <DatePicker style={{ width: '100%' }} placeholder="选择开始日期" />
                                </Form.Item>
                                <Form.Item
                                    label="生效结束日期（含）"
                                    name={[field.name, 'endDate']}
                                    getValueProps={(v) => ({ value: v ? dayjs(v) : undefined })}
                                    normalize={(v) => (v ? dayjs(v).format('YYYY-MM-DD') : undefined)}
                                >
                                    <DatePicker style={{ width: '100%' }} placeholder="选择结束日期" />
                                </Form.Item>
                                <Form.Item label="生效开始小时（含，0-23）" name={[field.name, 'startHour']}>
                                    <Select
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="选择开始小时"
                                        options={hourOptions}
                                    />
                                </Form.Item>
                                <Form.Item label="生效结束小时（不含，0-23）" name={[field.name, 'endHour']}>
                                    <Select
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="选择结束小时"
                                        options={hourOptions}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="骑手 ID 白名单（输入后回车，可单独删除）"
                                    name={[field.name, 'userIds']}
                                >
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        tokenSeparators={[',', ' ']}
                                        open={false}
                                        placeholder="输入骑手 ID 后回车，如 1001"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="仓库 ID 白名单（输入后回车，可单独删除）"
                                    name={[field.name, 'warehouseIds']}
                                >
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        tokenSeparators={[',', ' ']}
                                        open={false}
                                        placeholder="输入仓库 ID 后回车，如 100"
                                    />
                                </Form.Item>
                            </div>
                        ))}
                        <Button
                            type="dashed"
                            block
                            icon={<PlusOutlined />}
                            disabled={fields.length >= MAX_RULES_PER_ACTIVITY}
                            onClick={() => add({})}
                        >
                            {fields.length >= MAX_RULES_PER_ACTIVITY
                                ? `最多 ${MAX_RULES_PER_ACTIVITY} 条规则`
                                : '添加规则'}
                        </Button>
                    </>
                )}
            </Form.List>
        </Form>
    );

    // 手机模式用标准移动端逻辑视口宽度（390px），让响应式页面按手机布局回流，避免右侧被裁切。
    const isMobilePreview = previewMode === 'mobile';
    const MOBILE_WIDTH = 390;
    const MOBILE_HEIGHT = 844;
    const frameWidth = isMobilePreview ? MOBILE_WIDTH : '100%';
    const frameHeight = isMobilePreview ? MOBILE_HEIGHT : 480;

    const previewContent = (
        <div>
            <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Segmented
                    value={previewMode}
                    onChange={(v) => setPreviewMode(v as 'mobile' | 'desktop')}
                    options={[
                        { label: '手机', value: 'mobile' },
                        { label: '桌面', value: 'desktop' },
                    ]}
                />
                <span style={{ fontSize: 12, color: '#888', wordBreak: 'break-all', flex: 1 }}>{previewLink}</span>
            </div>
            {isMobilePreview && (
                <div style={{ marginBottom: 8, fontSize: 12, color: '#999' }}>
                    手机模式按 {MOBILE_WIDTH}px 宽度渲染；若外链页面非移动端自适应，右侧内容可能仍会溢出，可切换到「桌面」查看。
                </div>
            )}
            <div style={{
                display: 'flex', justifyContent: 'center',
                padding: isMobilePreview ? 12 : 0,
                maxHeight: isMobilePreview ? 620 : undefined,
                overflow: isMobilePreview ? 'auto' : undefined,
                background: isMobilePreview ? '#f5f5f5' : 'transparent',
                borderRadius: 8,
            }}>
                <iframe
                    key={`${previewLink}-${previewMode}`}
                    src={previewLink}
                    style={{
                        flex: 'none',
                        width: frameWidth,
                        height: frameHeight,
                        border: '1px solid #d9d9d9',
                        borderRadius: isMobilePreview ? 24 : 4,
                        background: '#fff',
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    title="活动预览"
                />
            </div>
        </div>
    );

    return (
        <Drawer
            title={isEdit ? '编辑活动配置' : '新建活动配置'}
            open={visible}
            onClose={onClose}
            width={600}
            destroyOnClose
            footer={
                <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" loading={submitting} onClick={handleSubmit}>
                        保存
                    </Button>
                </Space>
            }
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    { key: 'basic', label: '基本信息', children: basicTab },
                    { key: 'grayscale', label: '灰度规则', children: grayscaleTab },
                ]}
            />
            <Modal
                title="活动链接预览"
                open={previewModalOpen}
                onCancel={() => setPreviewModalOpen(false)}
                footer={null}
                width={460}
                destroyOnClose
            >
                {previewContent}
            </Modal>
        </Drawer>
    );
};

export default AppActivityDrawer;
